import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  PRODUCTS,
  MERCHANTS,
  getProductById,
  getMerchantById,
  calculateCommission,
  getRecommendations,
  getCrossCategoryRecommendations,
} from '../data/marketplaceData';

const MarketplaceContext = createContext(null);

const STORAGE_KEY = 'bonndle:marketplace';

// Generate session ID
const generateSessionId = () => `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export function MarketplaceProvider({ children }) {
  // Session state - tracks referral attribution
  const [session, setSession] = useState(() => {
    const stored = localStorage.getItem(`${STORAGE_KEY}:session`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        // Invalid stored data
      }
    }
    return {
      id: generateSessionId(),
      referralMerchantId: null,
      firstProductViewed: null,
      startedAt: Date.now(),
      productViewHistory: [],
    };
  });

  // Cart state
  const [cart, setCart] = useState(() => {
    const stored = localStorage.getItem(`${STORAGE_KEY}:cart`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        // Invalid stored data
      }
    }
    return {
      items: [],
      referralMerchantId: null,
    };
  });

  // UI state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [recentOrders, setRecentOrders] = useState(() => {
    const stored = localStorage.getItem(`${STORAGE_KEY}:orders`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        // Invalid stored data
      }
    }
    return [];
  });

  // Persist session
  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}:session`, JSON.stringify(session));
  }, [session]);

  // Persist cart
  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}:cart`, JSON.stringify(cart));
  }, [cart]);

  // Persist orders
  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}:orders`, JSON.stringify(recentOrders));
  }, [recentOrders]);

  // Track product view - sets referral merchant on first view
  const trackProductView = useCallback((productId) => {
    const product = getProductById(productId);
    if (!product) return;

    setSession(prev => {
      const newHistory = [...prev.productViewHistory];

      // Add to view history if not already viewed recently
      if (!newHistory.includes(productId)) {
        newHistory.push(productId);
        if (newHistory.length > 20) newHistory.shift(); // Keep last 20
      }

      // Set referral merchant on first product view of session
      if (!prev.referralMerchantId) {
        return {
          ...prev,
          referralMerchantId: product.merchantId,
          firstProductViewed: productId,
          productViewHistory: newHistory,
        };
      }

      return {
        ...prev,
        productViewHistory: newHistory,
      };
    });
  }, []);

  // Add to cart
  const addToCart = useCallback((productId, quantity = 1) => {
    const product = getProductById(productId);
    if (!product || quantity < 1) return false;

    // Track this as a view if not already tracked
    trackProductView(productId);

    setCart(prev => {
      const existingIndex = prev.items.findIndex(item => item.productId === productId);
      let newItems;

      if (existingIndex >= 0) {
        // Update quantity
        newItems = [...prev.items];
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          quantity: newItems[existingIndex].quantity + quantity,
        };
      } else {
        // Add new item
        newItems = [...prev.items, { productId, quantity }];
      }

      // Set referral merchant from session if not already set
      const referralMerchantId = prev.referralMerchantId || session.referralMerchantId;

      return {
        items: newItems,
        referralMerchantId,
      };
    });

    return true;
  }, [session.referralMerchantId, trackProductView]);

  // Update cart item quantity
  const updateCartQuantity = useCallback((productId, quantity) => {
    if (quantity < 1) {
      return removeFromCart(productId);
    }

    setCart(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.productId === productId ? { ...item, quantity } : item
      ),
    }));

    return true;
  }, []);

  // Remove from cart
  const removeFromCart = useCallback((productId) => {
    setCart(prev => ({
      ...prev,
      items: prev.items.filter(item => item.productId !== productId),
    }));
    return true;
  }, []);

  // Clear cart
  const clearCart = useCallback(() => {
    setCart({
      items: [],
      referralMerchantId: null,
    });
  }, []);

  // Calculate cart totals with commission breakdown
  const getCartTotals = useCallback(() => {
    const itemsWithProducts = cart.items.map(item => {
      const product = getProductById(item.productId);
      return {
        ...item,
        product,
        subtotal: product ? product.price * item.quantity : 0,
      };
    }).filter(item => item.product);

    const subtotal = itemsWithProducts.reduce((sum, item) => sum + item.subtotal, 0);
    const tax = subtotal * 0.08; // 8% tax
    const shipping = subtotal >= 50 ? 0 : 5.99; // Free shipping over $50
    const total = subtotal + tax + shipping;

    // Group by merchant for commission calculation
    const merchantGroups = {};
    itemsWithProducts.forEach(item => {
      const merchantId = item.product.merchantId;
      if (!merchantGroups[merchantId]) {
        merchantGroups[merchantId] = {
          merchantId,
          merchant: getMerchantById(merchantId),
          items: [],
          subtotal: 0,
        };
      }
      merchantGroups[merchantId].items.push(item);
      merchantGroups[merchantId].subtotal += item.subtotal;
    });

    // Calculate commissions for each merchant group
    const commissionBreakdown = Object.values(merchantGroups).map(group => {
      const commission = calculateCommission(
        group.subtotal,
        cart.referralMerchantId,
        group.merchantId
      );
      return {
        ...group,
        ...commission,
      };
    });

    return {
      items: itemsWithProducts,
      itemCount: itemsWithProducts.reduce((sum, item) => sum + item.quantity, 0),
      subtotal,
      tax,
      shipping,
      total,
      referralMerchantId: cart.referralMerchantId,
      referralMerchant: getMerchantById(cart.referralMerchantId),
      commissionBreakdown,
    };
  }, [cart]);

  // Complete checkout
  const checkout = useCallback(() => {
    const totals = getCartTotals();
    if (totals.items.length === 0) return null;

    const order = {
      id: `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      items: totals.items.map(item => ({
        productId: item.productId,
        productName: item.product.name,
        merchantId: item.product.merchantId,
        merchantName: getMerchantById(item.product.merchantId)?.name,
        price: item.product.price,
        quantity: item.quantity,
        subtotal: item.subtotal,
      })),
      subtotal: totals.subtotal,
      tax: totals.tax,
      shipping: totals.shipping,
      total: totals.total,
      referralMerchantId: totals.referralMerchantId,
      referralMerchantName: totals.referralMerchant?.name,
      commissionBreakdown: totals.commissionBreakdown.map(group => ({
        merchantId: group.merchantId,
        merchantName: group.merchant?.name,
        subtotal: group.subtotal,
        referralCommission: group.referralCommission,
        sellerAmount: group.sellerAmount,
        marketplaceCommission: group.marketplaceCommission,
      })),
      createdAt: Date.now(),
      status: 'completed',
    };

    // Add to orders
    setRecentOrders(prev => [order, ...prev].slice(0, 20));

    // Clear cart and start new session
    clearCart();
    setSession({
      id: generateSessionId(),
      referralMerchantId: null,
      firstProductViewed: null,
      startedAt: Date.now(),
      productViewHistory: [],
    });

    return order;
  }, [getCartTotals, clearCart]);

  // Get recommendations for current product or cart
  const getProductRecommendations = useCallback((productId) => {
    return getRecommendations(productId);
  }, []);

  const getCrossSellingRecommendations = useCallback((productId) => {
    return getCrossCategoryRecommendations(productId);
  }, []);

  // Get cart recommendations (based on last item added)
  const getCartRecommendations = useCallback(() => {
    if (cart.items.length === 0) return [];
    const lastItem = cart.items[cart.items.length - 1];
    return getCrossCategoryRecommendations(lastItem.productId, 4);
  }, [cart.items]);

  const value = {
    // Session
    session,
    trackProductView,

    // Cart
    cart,
    addToCart,
    updateCartQuantity,
    removeFromCart,
    clearCart,
    getCartTotals,
    showCart,
    setShowCart,

    // Checkout
    showCheckout,
    setShowCheckout,
    checkout,
    recentOrders,

    // UI state
    selectedProduct,
    setSelectedProduct,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    setSearchQuery,

    // Recommendations
    getProductRecommendations,
    getCrossSellingRecommendations,
    getCartRecommendations,

    // Data access
    products: PRODUCTS,
    merchants: MERCHANTS,
  };

  return (
    <MarketplaceContext.Provider value={value}>
      {children}
    </MarketplaceContext.Provider>
  );
}

export function useMarketplace() {
  const context = useContext(MarketplaceContext);
  if (!context) {
    throw new Error('useMarketplace must be used within a MarketplaceProvider');
  }
  return context;
}
